import React from 'react';
import { 
  Volume2, 
  PhoneOff, 
  Music, 
  DoorClosed, 
  LogIn,
  Shield
} from 'lucide-react';

const HouseRules = () => {
  const rules = [
    {
      icon: LogIn,
      title: 'Use Main Entrance',
      description: 'Always enter and exit through the main entrance for security purposes',
      color: 'blue'
    },
    {
      icon: Volume2,
      title: 'Maintain Complete Silence',
      description: 'Absolute silence must be maintained at all times to respect fellow students',
      color: 'sky'
    },
    {
      icon: PhoneOff,
      title: 'No Phone Calls Inside',
      description: 'Take all phone calls outside the study area to avoid disturbing others',
      color: 'indigo'
    },
    {
      icon: Music,
      title: 'No Music or Audio',
      description: 'Headphones allowed, but music and videos must be silent or inaudible to others',
      color: 'blue'
    },
    {
      icon: DoorClosed,
      title: 'Emergency Exit Protocol',
      description: 'Emergency doors are for emergencies only. Do not use for regular entry/exit',
      color: 'sky'
    },
    {
      icon: Shield,
      title: 'Security & Respect',
      description: 'Respect others\' space and belongings. Report any suspicious activity to staff',
      color: 'indigo'
    }
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      border: 'border-blue-200'
    },
    sky: {
      bg: 'bg-sky-50',
      icon: 'text-sky-600',
      border: 'border-sky-200'
    },
    indigo: {
      bg: 'bg-indigo-50',
      icon: 'text-indigo-600',
      border: 'border-indigo-200'
    }
  };

  return (
    <div className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
            Guidelines
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            House Rules
          </h2>
          <p className="text-lg text-gray-600">
            Simple guidelines to ensure a productive and respectful environment for everyone
          </p>
        </div>

        {/* Rules Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {rules.map((rule, index) => {
            const Icon = rule.icon;
            const colors = colorClasses[rule.color];
            
            return (
              <div
                key={index}
                className={`bg-white border-2 ${colors.border} rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
              >
                <div className={`${colors.bg} w-16 h-16 rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={colors.icon} size={32} />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {rule.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {rule.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Important Note */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Shield size={28} />
                Important Reminder
              </h3>
              <p className="text-blue-50 text-lg leading-relaxed">
                These rules are designed to create the best possible study environment for everyone. 
                By following these simple guidelines, we can maintain a peaceful, focused, and 
                productive space that helps you achieve your goals. Thank you for your cooperation!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HouseRules;
